<template>
  <v-container fluid>
    <v-row align="center">
      <v-col cols="12" sm="5">
        <v-autocomplete
          v-model="selectedPrincipal"
          v-model:search="principalSearch"
          :items="principalItems"
          :loading="loadingPrincipals"
          label="Usuario o cliente"
          prepend-inner-icon="mdi-account-search"
          :disabled="saving"
          clearable
          no-data-text="No hay coincidencias"
        />
      </v-col>
      <v-col cols="12" sm="4">
        <v-select
          v-model="selectedPermission"
          :items="PERMISSION_ITEMS"
          label="Permiso"
          prepend-inner-icon="mdi-key-variant"
          :disabled="saving"
        />
      </v-col>
      <v-col cols="12" sm="3">
        <v-btn
          variant="text"
          color="success"
          :loading="saving"
          :disabled="!canAdd"
          @click="addPermission"
        >
          <v-icon class="mr-1">mdi-plus</v-icon>Agregar
        </v-btn>
      </v-col>
    </v-row>

    <v-divider class="my-2" />

    <v-table density="comfortable">
      <thead>
        <tr>
          <th class="text-left">Principal</th>
          <th class="text-left">Permiso</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr v-if="loading">
          <td colspan="3"><v-progress-linear indeterminate color="primary" /></td>
        </tr>
        <tr v-else-if="permissions.length === 0">
          <td colspan="3" class="text-medium-emphasis">Sin permisos otorgados.</td>
        </tr>
        <tr v-for="(p, idx) in permissions" v-else :key="idx">
          <td>{{ principalLabel(p) }}</td>
          <td>
            <v-chip size="small" label>{{ permissionLabel(p.permission) }}</v-chip>
          </td>
          <td class="text-right">
            <v-btn
              icon="mdi-delete"
              variant="text"
              color="error"
              size="small"
              :disabled="saving"
              @click="removePermission(p)"
            />
          </td>
        </tr>
      </tbody>
    </v-table>
  </v-container>
</template>

<script setup lang="ts">
/**
 * Permisos de firma sobre un certificado.
 *
 * Hay DOS usos distintos de la lista de principals y mezclarlos rompe la vista:
 *
 *   - `catalog` es una caché acumulativa id -> Principal que NUNCA se vacía. De
 *     ahí salen los nombres de la tabla y la etiqueta del elemento seleccionado.
 *   - `searchResults` son los resultados de la última búsqueda, y sí se
 *     reemplazan en cada consulta.
 *
 * Usar un solo arreglo para ambos hacía que, al buscar o al seleccionar, la
 * consulta pisara la caché: la tabla perdía los nombres y mostraba ids crudos, y
 * el autocompletado mostraba el id porque el elegido ya no estaba entre sus
 * items. Además el catálogo quedaba filtrado a los principals que ya tenían
 * permiso, así que el desplegable sólo ofrecía esos dos.
 */
import { computed, onMounted, ref, watch } from "vue";
import type { CertificatePermission, Permission, Principal } from "bancojs";
import { banco } from "@/api/client";
import { useApiErrors } from "@/composables/useApiErrors";
import { useNotificationsStore } from "@/stores/notifications";
import { debounce } from "@/utils/functions";
import { permissionLabel, userTypeLabel } from "@/utils/labels";

const props = defineProps<{ fingerprint: string }>();

const { report } = useApiErrors();
const notifications = useNotificationsStore();

const PERMISSION_ITEMS = (
  ["ATTENDED_SIGN", "UNATTENDED_SIGN"] as CertificatePermission[]
).map((p) => ({ title: permissionLabel(p), value: p }));

const loading = ref(true);
const saving = ref(false);
const permissions = ref<Permission[]>([]);

const selectedPrincipal = ref<number | null>(null);
const selectedPermission = ref<CertificatePermission>("UNATTENDED_SIGN");
const canAdd = computed(() => selectedPrincipal.value != null && !!selectedPermission.value);

// ---- principals ----------------------------------------------------------

/** Caché acumulativa. Sólo crece: es la fuente de los nombres. */
const catalog = ref(new Map<number, Principal>());
const searchResults = ref<Principal[]>([]);
const loadingPrincipals = ref(false);
const principalSearch = ref("");

function remember(list: Principal[]) {
  const next = new Map(catalog.value);
  for (const p of list) next.set(p.id, p);
  catalog.value = next;
}

const describe = (p: Principal) => `${p.name} (${userTypeLabel(p.type)})`;

/**
 * Los items del desplegable. Siempre incluyen al seleccionado, aunque una
 * búsqueda posterior lo haya dejado fuera: si no, Vuetify no encuentra el valor
 * entre sus opciones y pinta el id crudo.
 */
const principalItems = computed(() => {
  const list = [...searchResults.value];
  const chosen = selectedPrincipal.value;
  if (chosen != null && !list.some((p) => p.id === chosen)) {
    const cached = catalog.value.get(chosen);
    if (cached) list.unshift(cached);
  }
  return list.map((p) => ({ title: describe(p), value: p.id }));
});

async function searchPrincipals(term: string) {
  loadingPrincipals.value = true;
  try {
    const page = await banco.user.principals.find({
      ...(term ? { name: term } : {}),
      size: 50,
    });
    searchResults.value = page.content;
    remember(page.content);
  } catch (err) {
    report(err, { title: "Permisos", fallback: "No fue posible buscar usuarios." });
  } finally {
    loadingPrincipals.value = false;
  }
}

const debouncedSearch = debounce((term: string) => void searchPrincipals(term), 300);

watch(principalSearch, (term) => {
  // Al seleccionar, Vuetify escribe el título del elegido en el buscador. Eso no
  // es una búsqueda del usuario: dispararla reemplazaría los resultados y
  // dejaría al seleccionado fuera de la lista.
  const chosen = selectedPrincipal.value;
  if (chosen != null && term === describe(catalog.value.get(chosen) ?? ({} as Principal))) {
    return;
  }
  debouncedSearch(term ?? "");
});

function principalLabel(permission: Permission): string {
  const id = "user_id" in permission ? permission.user_id : undefined;
  if (typeof id === "number") {
    const known = catalog.value.get(id);
    if (known) return describe(known);
    return `#${id}`;
  }
  return permission.user_username ?? "—";
}

// ---- carga y mutaciones --------------------------------------------------

async function loadPermissions() {
  const list = await banco.user.certificates.getPermissions(props.fingerprint);
  permissions.value = [...list].reverse();
}

async function load() {
  loading.value = true;
  try {
    // El catálogo se carga COMPLETO y sin filtrar: alimenta tanto la lista
    // inicial del desplegable como los nombres de la tabla.
    await Promise.all([loadPermissions(), searchPrincipals("")]);
  } catch (err) {
    report(err, {
      title: "Permisos",
      fallback: "Ocurrió un error al recuperar los permisos del certificado.",
    });
  } finally {
    loading.value = false;
  }
}

function resetSelection() {
  selectedPrincipal.value = null;
  principalSearch.value = "";
}

async function addPermission() {
  if (!canAdd.value) return;
  saving.value = true;
  try {
    // Responde 204 sin cuerpo: hay que recargar para ver el estado nuevo.
    await banco.user.certificates.addPermissions(props.fingerprint, [
      { user_id: selectedPrincipal.value as number, permission: selectedPermission.value },
    ]);
    notifications.success("Se otorgó el permiso.", "Permisos");
    resetSelection();
    await loadPermissions();
  } catch (err) {
    report(err, { title: "Permisos", fallback: "No fue posible otorgar el permiso." });
  } finally {
    saving.value = false;
  }
}

async function removePermission(permission: Permission) {
  saving.value = true;
  try {
    // DELETE con cuerpo: inusual, pero es lo que la API espera.
    await banco.user.certificates.destroyPermissions(props.fingerprint, [permission]);
    notifications.info("Se revocó el permiso.", "Permisos");
    await loadPermissions();
  } catch (err) {
    report(err, { title: "Permisos", fallback: "No fue posible revocar el permiso." });
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>
